/**
 * Versionsanzeige für GB-Sylt-Wissen
 * Zeigt: Version MAJOR.MINOR.PATCH · TT.MM.JJJJ
 * 
 * Version wird DYNAMISCH vom Backend geladen (/api/version)
 * Fallback auf Frontend-Version falls Backend nicht erreichbar
 */

import { useState, useEffect } from 'react';

// Fallback-Version (nur wenn Backend nicht erreichbar)
const FALLBACK_VERSION = "2.2.9";
const FALLBACK_BUILD_DATE = "28.12.2025";

// Cache für Version-Info (vermeidet mehrfache API-Calls)
let cachedVersionInfo = null;

export const VersionBadge = ({ className = "", showBuildInfo = false }) => {
  const [versionInfo, setVersionInfo] = useState(cachedVersionInfo || {
    version: FALLBACK_VERSION,
    buildDate: FALLBACK_BUILD_DATE,
    loading: !cachedVersionInfo
  });

  useEffect(() => {
    // Wenn bereits gecached, nicht erneut laden
    if (cachedVersionInfo) {
      setVersionInfo(cachedVersionInfo);
      return;
    }

    const fetchVersion = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/version`);
        
        if (response.ok) {
          const data = await response.json();
          const backendVersion = data.backend?.version || FALLBACK_VERSION;
          const buildDate = data.backend?.build_date || FALLBACK_BUILD_DATE;
          
          // Formatiere Datum zu TT.MM.JJJJ
          let formattedDate = buildDate;
          if (buildDate && buildDate.includes('-')) {
            const parts = buildDate.split('-');
            if (parts.length === 3) {
              formattedDate = `${parts[2]}.${parts[1]}.${parts[0]}`;
            }
          }
          
          const info = {
            version: backendVersion,
            buildDate: formattedDate,
            buildTimestamp: data.backend?.build_timestamp,
            loading: false,
            source: 'backend'
          };
          
          cachedVersionInfo = info;
          setVersionInfo(info);
        }
      } catch (error) {
        console.warn('Version fetch failed, using fallback:', error);
        const info = {
          version: FALLBACK_VERSION,
          buildDate: FALLBACK_BUILD_DATE,
          loading: false,
          source: 'fallback'
        };
        cachedVersionInfo = info;
        setVersionInfo(info);
      }
    };

    fetchVersion();
  }, []);

  return (
    <div className={`text-xs text-gray-500 dark:text-gray-400 ${className}`}>
      <span>
        Version {versionInfo.version} · {versionInfo.buildDate}
        {versionInfo.loading && ' ...'}
      </span>
      {showBuildInfo && versionInfo.buildTimestamp && (
        <span className="ml-1 text-gray-400 dark:text-gray-500">
          ({versionInfo.buildTimestamp.slice(0, 10)})
        </span>
      )}
    </div>
  );
};

export const getVersionInfo = async () => {
  if (cachedVersionInfo) {
    return {
      version: cachedVersionInfo.version,
      buildDate: cachedVersionInfo.buildDate,
      buildTimestamp: cachedVersionInfo.buildTimestamp,
      fullVersion: `Version ${cachedVersionInfo.version} · ${cachedVersionInfo.buildDate}`
    };
  }
  
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${API_URL}/api/version`);
    
    if (response.ok) {
      const data = await response.json();
      const version = data.backend?.version || FALLBACK_VERSION;
      const buildDate = data.backend?.build_date || FALLBACK_BUILD_DATE;
      const buildTimestamp = data.backend?.build_timestamp;
      
      return {
        version,
        buildDate,
        buildTimestamp,
        fullVersion: `Version ${version} · ${buildDate}`
      };
    }
  } catch (error) {
    console.warn('getVersionInfo failed:', error);
  }
  
  return {
    version: FALLBACK_VERSION,
    buildDate: FALLBACK_BUILD_DATE,
    buildTimestamp: null,
    fullVersion: `Version ${FALLBACK_VERSION} · ${FALLBACK_BUILD_DATE}`
  };
};

// Synchrone Version für Kompatibilität (verwendet Cache oder Fallback)
export const getVersionInfoSync = () => {
  if (cachedVersionInfo) {
    return {
      version: cachedVersionInfo.version,
      buildDate: cachedVersionInfo.buildDate,
      buildTimestamp: cachedVersionInfo.buildTimestamp,
      fullVersion: `Version ${cachedVersionInfo.version} · ${cachedVersionInfo.buildDate}`
    };
  }
  
  return {
    version: FALLBACK_VERSION,
    buildDate: FALLBACK_BUILD_DATE,
    buildTimestamp: null,
    fullVersion: `Version ${FALLBACK_VERSION} · ${FALLBACK_BUILD_DATE}`
  };
};

export default VersionBadge;
